/**
 * KioskSettingsMain
 *
 * Orchestrates all settings steps (info, photo, map,
 * station selection) and saving. Uses a custom hook for state.
 *
 * summary:
 * - Central hub composing all settings sub-components
 * - Tracks unsaved changes and warns on page unload
 * - Opens map dialog, manages languages, drag/drop order, and persistence
 */
import React, { useEffect } from "react";
import { Box, Typography, Alert, Snackbar, Stack, Button } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useNavigate } from "react-router-dom";

// Local imports
import { SNACKBAR_AUTO_HIDE_DURATION } from "../../utils/constants";
import { isValidMapConfig } from "../../utils/helpers";
import MapSelectionDialog from "../../MapSelectionDialog";

// Settings components
import InfoSponsorSlide from "./InfoSponsorSlide";
import AnnouncementPhotoConfig from "./AnnouncementPhotoConfig";
import MapConfigSlide from "./MapConfigSlide";
import StationSlidesConfig from "./StationSlidesConfig";
import ConfigurationStatus from "./ConfigurationStatus";
import SaveButton from "./SaveButton";
import ModalMessageDialog from "./ModalMessageDialog";

// Custom hook
import useKioskSettings from "./useKioskSettings";

/**
 * KioskSettingsMain Component
 *
 * Main component for managing kiosk settings using modular components.
 * This is the optimized version of the original KioskCommunitySetting component.
 *
 * @component
 */
export default function KioskSettingsMain() {
  const {
    // State
    community,
    rows,
    snackbarOpen,
    loading,
    error,
    notConfigured,
    mapConfig,
    mapLanguages,
    showWindBarbMap,
    windBarbMapLanguages,
    mapDialogOpen,
    lastModified,
    selectedStations,
    draggedStation,
    announcementPhotoUrl,
    pendingImageFile,
    statusOptions,
    typeOptions,
    communityOptions,
    filteredRows,
    filters,
    search,
    introImages,
    hasUnsavedChanges,

    // Handlers
    handleDisplayChange,
    handleLanguageChange,
    handleTimerChange,
    handleSearchChange,
    handleFilterChange,
    handleMapLanguageChange,
    handleWindBarbMapToggle,
    handleWindBarbMapLanguageChange,
    handleMapDialogOpen,
    handleMapDialogClose,
    handleMapAreaSelect,
    handleDeleteMap,
    moveStationUp,
    moveStationDown,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleSave,
    handleSnackbarClose,
    formatLastModified,
    handlePreviewPhoto,
    handleFileSelect,
    handleRemovePhoto,
  } = useKioskSettings();
  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalType, setModalType] = React.useState("success");
  const [modalMessage, setModalMessage] = React.useState("");

  // Show modal on success or error
  React.useEffect(() => {
    if (snackbarOpen) {
      setModalType("success");
      setModalMessage("Slideshow configuration saved successfully!");
      setModalOpen(true);
    } else if (Boolean(error) && !notConfigured) {
      setModalType("error");
      setModalMessage("Something went wrong. Please try again.");
      setModalOpen(true);
    }
  }, [snackbarOpen, error, notConfigured]);

  const handleModalClose = () => {
    setModalOpen(false);
    handleSnackbarClose();
  };

  // Warn user if there are unsaved changes on reload/close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
        return e.returnValue;
      }
    };
    if (hasUnsavedChanges) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    } else {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }

  // Community not found state
  if (!community) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h6">Community not found.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        p: { xs: 1, sm: 3 },
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* Main Content Container */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <Box sx={{ maxWidth: "70%", mx: "auto", p: 2 }}>
          {/* Configuration Status */}
          <ConfigurationStatus
            notConfigured={notConfigured}
            error={error}
            showError={!notConfigured}
          />

          {/* Top Row - Title */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "sticky",
              top: 0,
              bgcolor: "background.paper",
              zIndex: 10,
              mb: 4,
              borderColor: "divider",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "primary.main",
                textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                letterSpacing: "-0.5px",
              }}
            >
              {community.name} - Slideshow Configuration
            </Typography>
            <Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayArrowIcon />}
                onClick={() => navigate(`/kiosk/${community.communityId}/play`)}
                sx={{ ml: 2, fontWeight: 600, borderRadius: 3 }}
              >
                Play Mode
              </Button>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1 }}>
            <Stack spacing={3}>
              {/* Slide 1: Info/Sponsor Slide */}
              <InfoSponsorSlide introImages={introImages} />

              {/* Announcement Photo Configuration */}
              <AnnouncementPhotoConfig
                announcementPhotoUrl={announcementPhotoUrl}
                pendingImageFile={pendingImageFile}
                loading={loading}
                onFileSelect={handleFileSelect}
                onPreviewPhoto={handlePreviewPhoto}
                onRemovePhoto={handleRemovePhoto}
              />

              {/* Slide 2: Map Configuration */}
              <MapConfigSlide
                mapConfig={mapConfig}
                isValidMapConfig={isValidMapConfig}
                onMapDialogOpen={handleMapDialogOpen}
                onDeleteMap={handleDeleteMap}
                mapLanguages={mapLanguages}
                showWindBarbMap={showWindBarbMap}
                onWindBarbMapToggle={handleWindBarbMapToggle}
                windBarbMapLanguages={windBarbMapLanguages}
                onWindBarbMapLanguageChange={handleWindBarbMapLanguageChange}
              />

              {/* Slide 3+: Station Slides with integrated table */}
              <StationSlidesConfig
                selectedStations={selectedStations}
                draggedStation={draggedStation}
                rows={rows}
                filteredRows={filteredRows}
                filters={filters}
                search={search}
                statusOptions={statusOptions}
                typeOptions={typeOptions}
                communityOptions={communityOptions}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onMoveUp={moveStationUp}
                onMoveDown={moveStationDown}
                onDisplayChange={handleDisplayChange}
                onTimerChange={handleTimerChange}
                onSearchChange={handleSearchChange}
                onFilterChange={handleFilterChange}
                onLanguageChange={handleLanguageChange}
              />
            </Stack>
          </Box>
        </Box>

        {/* Sticky Bottom Bar - Save Button and Last Modified */}
        <SaveButton
          loading={loading}
          onSave={handleSave}
          lastModified={lastModified}
          formatLastModified={formatLastModified}
          hasUnsavedChanges={hasUnsavedChanges}
        />
      </Box>

      {/* Modal Message Dialog for Success/Error */}
      <ModalMessageDialog
        open={modalOpen}
        onClose={handleModalClose}
        type={modalType}
        message={modalMessage}
      />

      {/* Map Selection Dialog */}
      <MapSelectionDialog
        open={mapDialogOpen}
        onClose={handleMapDialogClose}
        onMapAreaSelect={handleMapAreaSelect}
        currentMapConfig={isValidMapConfig(mapConfig) ? mapConfig : null}
        mapLanguages={mapLanguages}
        onMapLanguageChange={handleMapLanguageChange}
        showWindBarbMap={showWindBarbMap}
        onWindBarbMapToggle={handleWindBarbMapToggle}
        windBarbMapLanguages={windBarbMapLanguages}
        onWindBarbMapLanguageChange={handleWindBarbMapLanguageChange}
      />
    </Box>
  );
}
